<script setup lang="ts">

import CoinIcon from '@/components/icons/CoinIcon.vue'
import { formatNumberFullTruncate } from '@/helpers/utils'
import CheckCircleGreenBorder from '@/components/icons/CheckCircleGreenBorder.vue'
import { computed } from 'vue'
import SpinnerIcon from '@/components/icons/SpinnerIcon.vue'
import LevelProgressBar from '@/components/LevelProgressBar.vue'
import { twMerge } from 'tailwind-merge'
import ChevronRightIcon from '@/components/icons/ChevronRightIcon.vue'
import SpinImage from '@/assets/images/fortune-wheel-without-border.png'
import TicketImage from '@/assets/images/emojis/emoji-ticket.png'
import { T } from '@tolgee/vue'
import { useClaimTask } from '@/composables/game/claim-task'
import Clock2Icon from '@/components/icons/Clock2Icon.vue'
import { mysteryTaskCategory, taskIdsWithoutManualClaim, useMysteryTask } from '@/composables/game/tasks'
import { formatDuration } from '@/helpers/date'
import { useOnboardingFingerEmojis } from '@/composables/game/onboarding'
import FingerUpGif from '@/assets/images/gifs/finger-up.gif'

const props = defineProps<{
	id?: string
	imageUrl: string
	title: string
	reward?: number
	spin?: number
	tickets?: number
	showTickets?: boolean
	isDone: boolean
	progress?: { done: number, max: number }
	hideProgress?: boolean
	claiming?: boolean
	forceShowClaimButton?: boolean,
	imageClass?: string
	actionCompletedAt?: number
	claimDelay?: number
	error?: string
	middleContainerClass?: string
	hasManualVerification?: boolean
	isVerified?: boolean
	categories?: string[]
	startsOn?: number | null
	index?: number
}>()

defineEmits(['claim'])

const { canShowOnboardingEmoji, showOnboardingEmojiQuest } = useOnboardingFingerEmojis()

const { hasOpenedUrl, remainedSecondsUntilClaim, claimDelayFormattedTime } = useClaimTask(computed(() => ({
  id: props.id,
  actionCompletedAt: props.actionCompletedAt,
  claimDelay: props.claimDelay,
})))

const isMysteryTask = computed(() => props.categories?.includes(mysteryTaskCategory) ?? false)
const isTestTask = computed(() => props.categories?.includes('test') ?? false)
const { getCountDownSeconds } = useMysteryTask()
const taskCountDownSeconds = computed(() => {
  if (!isMysteryTask.value || !props.startsOn) return 0
  return getCountDownSeconds(props.startsOn)
})
const formattedCountDown = computed(() => formatDuration(taskCountDownSeconds.value))

const progressPercentage = computed(() => {
  if (!props.progress) return 0
  return Math.round(Math.min(props.progress.done, props.progress.max) * 100 / props.progress.max)
})

const progressIsDone = computed(() => !!props.progress && props.progress.done >= props.progress.max)
const showPendingVerification = computed(() => props.hasManualVerification && !props.isVerified)
const hasManualClaim = computed(() => !taskIdsWithoutManualClaim.includes(props.id ?? ''))
const showClaimSection = computed(() => (!props.isDone && (progressIsDone.value || props.forceShowClaimButton || hasOpenedUrl.value && hasManualClaim.value)))
const canClaimAfterClickAction = computed(() => hasOpenedUrl.value && !showPendingVerification.value && !remainedSecondsUntilClaim.value)
const canClaim = computed(() => hasManualClaim.value && !props.isDone && (progressIsDone.value || canClaimAfterClickAction.value))
</script>

<template>
  <div
    class="h-[76px] py-2 px-4 rounded-xl flex items-center justify-between min-w-0 bg-dark-bottom-layer relative"
    :class="isDone ? 'opacity-[0.76] outline outline-1 outline-green-primary' : ''"
    @click="showOnboardingEmojiQuest = false"
  >
    <img
      v-if="index === 0 && canShowOnboardingEmoji && showOnboardingEmojiQuest"
      class="pointer-events-none absolute -bottom-[22px] left-1/2 translate-x-[calc(-50%+30px)]  size-[88px] rotate-[-45deg] object-cover z-[1]"
      :src="FingerUpGif"
    >
    <div class="rounded-full w-8 h-8 min-w-[32px] max-w-[32px] mr-4 relative">
      <img
        alt="Task image"
        :class="twMerge('absolute inset-0 w-full h-full object-cover scale-in rounded-[2px]', imageClass)"
        :src="imageUrl"
      >
    </div>
    <div :class="twMerge('flex-1 flex flex-col gap-y-1 mr-4 min-w-0', middleContainerClass)">
      <div class="flex flex-wrap items-center">
        <div class="text-base leading-[18px] -tracking-[0.32px] font-semibold text-white">
          {{ title }}
          <template v-if="isTestTask">
            - test
          </template>
        </div>
        <div
          v-if="isMysteryTask"
          class="scale-up-light ml-2.5 flex rounded-full px-1.5 py-0.5 bg-white text-tropee-black text-[10px] leading-[14px] font-bold -tracking-[0.3px]"
        >
          {{ formattedCountDown }}
        </div>
      </div>
      <div class="flex items-center gap-x-1 min-w-max">
        <div
          v-if="reward"
          class="flex items-center gap-x-1 text-solid-white"
        >
          <CoinIcon class="h-4 min-w-[10px] w-auto" />
          <span class="text-xs font-semibold -tracking-[0.24px]">+{{
            formatNumberFullTruncate(reward)
          }}</span>
        </div>
        <span
          v-if="reward && spin"
          class="text-xs text-white/60 font-semibold -tracking-[0.28px]"
        >+</span>
        <div
          v-if="spin"
          class="flex items-center gap-x-1 whitespace-nowrap"
        >
          <img
            :src="SpinImage"
            class="size-4"
          >
          <span class="text-xs font-semibold text-solid-white -tracking-[0.24px]">
            {{ formatNumberFullTruncate(spin) }}
          </span>
        </div>
        <span
          v-if="tickets && (!!reward || !!spin)"
          class="text-xs text-white/60 font-semibold -tracking-[0.28px]"
        >+</span>
        <div
          v-if="tickets"
          class="flex items-center gap-x-1 whitespace-nowrap"
        >
          <img
            :src="TicketImage"
            class="size-4"
          >
          <span class="text-xs font-semibold text-solid-white -tracking-[0.24px]">
            {{ formatNumberFullTruncate(tickets) }}
          </span>
        </div>
      </div>
      <LevelProgressBar
        v-if="progress && !hideProgress"
        :progress="progressPercentage"
        progress-bar-class="bg-dark-top-layer relative"
      >
        <div
          :class="twMerge('absolute top-1/2 -translate-y-1/2 right-4 text-xs leading-[14px] font-medium -tracking-[0.24px] text-white/[38%]',
                          progress.done >= progress.max && 'text-black')"
        >
          {{ Math.min(progress.done, progress.max) }}/{{ progress.max }}
        </div>
      </LevelProgressBar>
    </div>
    <div class="ml-auto min-w-[64px] flex items-center justify-end">
      <CheckCircleGreenBorder
        v-if="isDone"
        class="w-10 h-10"
      />
      <div
        v-else-if="showClaimSection"
        class="w-full min-w-full h-7 flex items-center justify-center rounded-md active:bg-white/[.24] text-white/[.87] uppercase claim-shadow text-[14px] leading-[14px] font-bold"
        :class="twMerge('bg-white/[.12]', !canClaim && 'pointer-events-none opacity-[0.32]', remainedSecondsUntilClaim !== 0 && !showPendingVerification && 'opacity-[0.32]', showPendingVerification && 'opacity-100 bg-white/[.04]')"
        @click.stop="$emit('claim')"
      >
        <SpinnerIcon
          v-if="claiming"
          class="w-[14px] h-[14px] min-w-[14px]"
        />
        <Clock2Icon
          v-else-if="showPendingVerification"
          class="w-5 h-5 min-w-[20px]"
        />
        <span
          v-else-if="claimDelayFormattedTime"
          class="-ml-1 text-center flex items-center justify-center"
          style="font-variant: tabular-nums;"
        >&nbsp;	{{
          claimDelayFormattedTime
        }}</span>
        <span
          v-else
          class="px-2"
        >
          <T
            v-if="isMysteryTask"
            key-name="quests.check"
            default-value="Check"
          />
          <T
            v-else
            key-name="quests.claim"
            default-value="Claim"
          />
        </span>
      </div>
      <ChevronRightIcon
        v-else
        class="w-6 h-6"
      />
    </div>
  </div>
</template>

<style scoped>
.claim-shadow {
	text-shadow: 0px 0px 56px rgba(0, 0, 0, 0.50);
}
</style>